/**
 * W4 §9.2 — visibility-aware idle + absolute session timers.
 */
import { useEffect, useRef } from 'react';
import type { RuntimeConfig } from '../../config/runtimeConfig.js';

const SESSION_STARTED_KEY = 'rpapp_customer_session_started_at';

const ACTIVITY_EVENTS: readonly (keyof WindowEventMap)[] = [
  'pointerdown',
  'keydown',
  'scroll',
  'touchstart',
] as const;

export function markCustomerSessionStarted(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(SESSION_STARTED_KEY, String(Date.now()));
}

export function clearCustomerSessionStarted(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(SESSION_STARTED_KEY);
}

function readSessionStartedAt(): number | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  const raw = sessionStorage.getItem(SESSION_STARTED_KEY);
  if (raw === null) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function useCustomerSessionTimeouts(input: {
  enabled: boolean;
  config: RuntimeConfig;
  onIdleTimeout: () => void;
  onAbsoluteTimeout: () => void;
}): void {
  const idleMs = input.config.customerIdleTimeoutMs;
  const absoluteMs = input.config.customerAbsoluteSessionMaxMs;
  const lastActivityRef = useRef(0);
  const onIdleRef = useRef(input.onIdleTimeout);
  const onAbsoluteRef = useRef(input.onAbsoluteTimeout);

  useEffect(() => {
    onIdleRef.current = input.onIdleTimeout;
    onAbsoluteRef.current = input.onAbsoluteTimeout;
  }, [input.onAbsoluteTimeout, input.onIdleTimeout]);

  useEffect(() => {
    if (!input.enabled || typeof window === 'undefined') {
      return;
    }

    lastActivityRef.current = Date.now();

    const touchActivity = (): void => {
      lastActivityRef.current = Date.now();
    };

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, touchActivity, { passive: true });
    }

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      const now = Date.now();
      const startedAt = readSessionStartedAt();
      if (startedAt !== null && now - startedAt >= absoluteMs) {
        onAbsoluteRef.current();
        return;
      }
      if (now - lastActivityRef.current >= idleMs) {
        onIdleRef.current();
      }
    }, 30_000);

    return () => {
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, touchActivity);
      }
      window.clearInterval(intervalId);
    };
  }, [absoluteMs, idleMs, input.enabled]);
}
